// src/pages/production/SettingsTab.tsx
//
// Phase 2A: JSON export / import / clear.
// Phase 2D-lite: source picker per collection (Local working, Advantage +
// DuckDB stubbed for Phase 3).

import { useState } from 'react';
import { Download, Upload, Trash2, HardDrive, Database, Cloud, AlertCircle, Sparkles, FileSpreadsheet } from 'lucide-react';
import Modal from '../../components/Modal';
import {
  exportAllData, importAllData, clearAllData, loadSampleData,
  getStorageConfig, setStorageConfig,
  getItems, saveItem,
} from '../../services/productionStorage';
import { clearTenantSeededFlag } from '../../services/productionSeedData';
import {
  downloadItemsTemplate, parseItemsWorkbook, type ParsedItemRow,
  downloadForecastTemplate,
} from '../../services/productionExcel';
import type { ProductionStorageBackend, ProductionStorageConfig } from '../../types/production';

interface SettingsTabProps {
  slug: string;
}

const BACKENDS: Array<{ id: ProductionStorageBackend; label: string; icon: React.ComponentType<{ size?: number }>; description: string; disabled: boolean }> = [
  { id: 'local',     label: 'Local',     icon: HardDrive, description: 'Browser localStorage, per tenant. No backend required.', disabled: false },
  { id: 'advantage', label: 'Advantage', icon: Database,  description: 'Read-only mirror from the tenant\'s Advantage cache (Phase 3).', disabled: true },
  { id: 'duckdb',    label: 'DuckDB',    icon: Cloud,     description: 'Local DuckDB WASM for larger datasets (Phase 3).', disabled: true },
];

export default function SettingsTab({ slug }: SettingsTabProps) {
  const [config, setConfig] = useState<ProductionStorageConfig>(() => getStorageConfig(slug));
  const [confirmClear, setConfirmClear] = useState(false);
  const [confirmSeed, setConfirmSeed] = useState(false);
  const [importPreview, setImportPreview] = useState<{ raw: string; counts: Record<string, number> } | null>(null);
  const [excelItemPreview, setExcelItemPreview] = useState<{ rows: ParsedItemRow[]; warnings: string[] } | null>(null);

  function updateBackend(coll: keyof ProductionStorageConfig, backend: ProductionStorageBackend) {
    const next = { ...config, [coll]: backend };
    setConfig(next);
    setStorageConfig(slug, next);
  }

  function handleExport() {
    const data = exportAllData(slug);
    const json = JSON.stringify(data, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    const a = document.createElement('a');
    a.href = url;
    a.download = `production-${slug}-${stamp}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function handleImportPick() {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json,application/json';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      const text = await file.text();
      try {
        const parsed = JSON.parse(text);
        const counts: Record<string, number> = {
          items:    Array.isArray(parsed.items)    ? parsed.items.length    : 0,
          recipes:  Array.isArray(parsed.recipes)  ? parsed.recipes.length  : 0,
          products: Array.isArray(parsed.products) ? parsed.products.length : 0,
          forecast: Array.isArray(parsed.forecast) ? parsed.forecast.length : 0,
        };
        setImportPreview({ raw: text, counts });
      } catch {
        alert('Could not parse JSON — file is malformed.');
      }
    };
    input.click();
  }

  function handleImportConfirm() {
    if (!importPreview) return;
    try {
      const parsed = JSON.parse(importPreview.raw);
      importAllData(slug, parsed);
      setImportPreview(null);
      window.location.reload();
    } catch (e) {
      alert(`Import failed: ${e instanceof Error ? e.message : 'unknown error'}`);
    }
  }

  function handleClearConfirm() {
    clearAllData(slug);
    clearTenantSeededFlag(slug);  // so a fresh visit re-seeds if user wants
    setConfirmClear(false);
    window.location.reload();
  }

  function handleSeedConfirm() {
    loadSampleData(slug, false);
    setConfirmSeed(false);
    window.location.reload();
  }

  function handleItemsExcelPick() {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      const buf = await file.arrayBuffer();
      const parsed = parseItemsWorkbook(buf, getItems(slug));
      setExcelItemPreview(parsed);
    };
    input.click();
  }

  function handleItemsExcelConfirm() {
    if (!excelItemPreview) return;
    for (const r of excelItemPreview.rows) {
      saveItem(slug, {
        id: r.existingId,
        name: r.name,
        category: r.category,
        unit: r.unit,
        costPerUnit: r.costPerUnit,
        onHand: r.onHand,
        description: r.description,
      });
    }
    setExcelItemPreview(null);
    window.location.reload();
  }

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-bold text-white">Settings</h2>
        <p className="text-sm text-slate-400 mt-0.5">
          Data source per collection, plus export / import / clear for the active tenant.
        </p>
      </div>

      {/* Source picker per collection */}
      <div className="mb-8">
        <h3 className="text-sm font-medium text-slate-300 mb-3">Data source per collection</h3>
        <div className="grid gap-3">
          {(['items', 'recipes', 'products'] as const).map((coll) => (
            <div key={coll} className="p-4 rounded-2xl bg-slate-800/30 border border-slate-700/40">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <span className="text-white font-medium capitalize">{coll}</span>
                  <span className="text-xs text-slate-500 ml-2">tenant: {slug}</span>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {BACKENDS.map((b) => {
                  const Icon = b.icon;
                  const active = config[coll] === b.id;
                  return (
                    <button
                      key={b.id}
                      onClick={() => !b.disabled && updateBackend(coll, b.id)}
                      disabled={b.disabled}
                      title={b.disabled ? `${b.label} backend coming in Phase 3` : b.description}
                      className={`text-left p-3 rounded-lg border transition-all ${
                        active
                          ? 'bg-orange-500/10 border-orange-500/40 text-orange-200'
                          : b.disabled
                            ? 'bg-slate-900/40 border-slate-700/40 text-slate-600 cursor-not-allowed opacity-60'
                            : 'bg-slate-800/40 border-slate-700/40 text-slate-300 hover:border-slate-600/50'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <Icon size={14} />
                        <span className="text-sm font-medium">{b.label}</span>
                        {b.disabled && <span className="text-[9px] uppercase tracking-wider text-slate-500">P3</span>}
                      </div>
                      <p className="text-xs text-slate-400 leading-snug">{b.description}</p>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        <p className="text-xs text-slate-500 italic mt-2">
          Forecast always uses Local — it's per-user planning state, not source-of-truth.
        </p>
      </div>

      {/* Sample data */}
      <div className="mb-8">
        <h3 className="text-sm font-medium text-slate-300 mb-3">Sample dataset</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <ActionTile
            onClick={() => setConfirmSeed(true)}
            icon={Sparkles}
            title="Load sample data"
            description="Adds 29 sample items, 5 recipes, and 2 products to this tenant. Existing data is preserved — duplicates by id are skipped."
            accent="orange"
          />
          <div className="p-4 rounded-2xl bg-slate-800/30 border border-slate-700/40 text-xs text-slate-400 leading-snug">
            <p className="text-white text-sm font-medium mb-1">First-visit auto-seed</p>
            <p>If a tenant has no items AND no recipes on first visit, the sample dataset is loaded automatically so you can explore the app. After that, the data is yours.</p>
          </div>
        </div>
      </div>

      {/* Excel import */}
      <div className="mb-8">
        <h3 className="text-sm font-medium text-slate-300 mb-3">Excel import / templates</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="p-4 rounded-2xl bg-slate-800/30 border border-slate-700/40 space-y-2">
            <div className="flex items-center gap-2 text-white font-medium">
              <FileSpreadsheet size={16} />
              Items
            </div>
            <p className="text-xs text-slate-400 leading-snug">
              Bulk-import raw materials with cost + on-hand. Existing items (matched by name) are updated; new names create new items.
            </p>
            <div className="flex gap-2 mt-2">
              <button
                onClick={() => downloadItemsTemplate(getItems(slug))}
                className="px-3 py-1.5 rounded-lg bg-slate-700 hover:bg-slate-600 text-white text-xs"
              >
                Download template
              </button>
              <button
                onClick={handleItemsExcelPick}
                className="px-3 py-1.5 rounded-lg bg-orange-500 hover:bg-orange-400 text-white text-xs font-medium"
              >
                Upload .xlsx
              </button>
            </div>
          </div>
          <div className="p-4 rounded-2xl bg-slate-800/30 border border-slate-700/40 space-y-2">
            <div className="flex items-center gap-2 text-white font-medium">
              <FileSpreadsheet size={16} />
              Forecast
            </div>
            <p className="text-xs text-slate-400 leading-snug">
              Download a forecast template populated with your current products. Upload the filled file from Planning → Import forecast.
            </p>
            <div className="flex gap-2 mt-2">
              <button
                onClick={() => {
                  // Pull product names from local — sufficient for the template seed
                  const items = getItems(slug);
                  // Use first 5 item names as example placeholders if no products exist
                  // (Settings doesn't have getProducts in scope yet, so pull from window)
                  downloadForecastTemplate([]);
                  void items;
                }}
                className="px-3 py-1.5 rounded-lg bg-slate-700 hover:bg-slate-600 text-white text-xs"
              >
                Download template
              </button>
              <span className="text-xs text-slate-500 self-center">Upload on Planning tab</span>
            </div>
          </div>
        </div>
        <p className="text-xs text-slate-500 italic mt-2">
          Recipes and Products import is multi-row (components / packaging) and lands in a separate PR.
        </p>
      </div>

      {/* Backup / restore */}
      <div className="mb-8">
        <h3 className="text-sm font-medium text-slate-300 mb-3">Backup &amp; restore</h3>
        <div className="grid sm:grid-cols-3 gap-3">
          <ActionTile
            onClick={handleExport}
            icon={Download}
            title="Export JSON"
            description="Download all data for this tenant as a single JSON file."
            accent="emerald"
          />
          <ActionTile
            onClick={handleImportPick}
            icon={Upload}
            title="Import JSON"
            description="Replace this tenant's data with a previously exported JSON file."
            accent="blue"
          />
          <ActionTile
            onClick={() => setConfirmClear(true)}
            icon={Trash2}
            title="Clear all data"
            description="Wipe items, recipes, products, and forecast for this tenant. Cannot be undone."
            accent="red"
          />
        </div>
      </div>

      {importPreview && (
        <Modal onClose={() => setImportPreview(null)} panelClassName="max-w-md">
          <h3 className="text-lg font-semibold text-white mb-2">Confirm import</h3>
          <p className="text-sm text-slate-400 mb-4">
            About to <span className="text-amber-300 font-medium">replace</span> all
            <code className="px-1 py-0.5 mx-1 rounded bg-slate-800 text-slate-200 font-mono">{slug}</code>
            data with:
          </p>
          <ul className="text-sm text-slate-200 mb-6 space-y-1">
            <li>• <span className="font-mono">{importPreview.counts.items}</span> items</li>
            <li>• <span className="font-mono">{importPreview.counts.recipes}</span> recipes</li>
            <li>• <span className="font-mono">{importPreview.counts.products}</span> products</li>
            <li>• <span className="font-mono">{importPreview.counts.forecast}</span> forecast lines</li>
          </ul>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setImportPreview(null)}
              className="px-4 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-white text-sm"
            >
              Cancel
            </button>
            <button
              onClick={handleImportConfirm}
              className="px-4 py-2 rounded-lg bg-blue-500 hover:bg-blue-400 text-white text-sm font-medium"
            >
              Import &amp; reload
            </button>
          </div>
        </Modal>
      )}

      {excelItemPreview && (
        <Modal onClose={() => setExcelItemPreview(null)} panelClassName="max-w-2xl">
          <h3 className="text-lg font-semibold text-white mb-2">Items Excel preview</h3>
          {excelItemPreview.warnings.length > 0 && (
            <div className="mb-3 p-3 rounded bg-amber-500/10 border border-amber-500/30 text-amber-200 text-xs space-y-1">
              {excelItemPreview.warnings.map((w, i) => <div key={i}>• {w}</div>)}
            </div>
          )}
          {excelItemPreview.rows.length === 0 ? (
            <p className="text-sm text-slate-400 mb-6">No valid rows found. Check the column headers in row 1.</p>
          ) : (
            <>
              <p className="text-sm text-slate-400 mb-3">
                {excelItemPreview.rows.length} row(s) ready —
                <span className="text-orange-300 ml-1">
                  {excelItemPreview.rows.filter((r) => r.existingId).length} update(s)
                </span>,
                <span className="text-emerald-300 ml-1">
                  {excelItemPreview.rows.filter((r) => !r.existingId).length} new
                </span>.
              </p>
              <div className="max-h-[40vh] overflow-y-auto rounded border border-slate-700/40">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-slate-900">
                    <tr className="text-left text-slate-400">
                      <th className="px-3 py-2">Name</th>
                      <th className="px-3 py-2">Cat</th>
                      <th className="px-3 py-2 text-right">Cost</th>
                      <th className="px-3 py-2">Unit</th>
                      <th className="px-3 py-2 text-right">On hand</th>
                      <th className="px-3 py-2">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800">
                    {excelItemPreview.rows.map((r, i) => (
                      <tr key={i}>
                        <td className="px-3 py-1 text-white">{r.name}</td>
                        <td className="px-3 py-1 text-slate-300">{r.category || '—'}</td>
                        <td className="px-3 py-1 text-right font-mono text-slate-200">${r.costPerUnit.toFixed(4)}</td>
                        <td className="px-3 py-1 text-slate-400">{r.unit}</td>
                        <td className="px-3 py-1 text-right font-mono text-slate-300">{r.onHand ?? '—'}</td>
                        <td className="px-3 py-1">
                          {r.existingId ? (
                            <span className="text-orange-300">update</span>
                          ) : (
                            <span className="text-emerald-300">new</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
          <div className="mt-6 flex justify-end gap-2">
            <button
              onClick={() => setExcelItemPreview(null)}
              className="px-4 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-white text-sm"
            >
              Cancel
            </button>
            <button
              onClick={handleItemsExcelConfirm}
              disabled={excelItemPreview.rows.length === 0}
              className="px-4 py-2 rounded-lg bg-orange-500 hover:bg-orange-400 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium"
            >
              Import {excelItemPreview.rows.length} row(s)
            </button>
          </div>
        </Modal>
      )}

      {confirmSeed && (
        <Modal onClose={() => setConfirmSeed(false)} panelClassName="max-w-md">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="text-orange-400" size={20} />
            <h3 className="text-lg font-semibold text-white">Load sample data?</h3>
          </div>
          <p className="text-sm text-slate-400 mb-2">
            Adds the sample dataset (29 items, 5 recipes, 2 products) to tenant
            <code className="px-1 py-0.5 mx-1 rounded bg-slate-800 text-slate-200 font-mono">{slug}</code>.
          </p>
          <p className="text-sm text-slate-400 mb-6">
            Existing rows are kept. Duplicates (matched by id) are skipped — safe to re-run.
          </p>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setConfirmSeed(false)}
              className="px-4 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-white text-sm"
            >
              Cancel
            </button>
            <button
              onClick={handleSeedConfirm}
              className="px-4 py-2 rounded-lg bg-orange-500 hover:bg-orange-400 text-white text-sm font-medium"
            >
              Load &amp; reload
            </button>
          </div>
        </Modal>
      )}

      {confirmClear && (
        <Modal onClose={() => setConfirmClear(false)} panelClassName="max-w-md">
          <div className="flex items-center gap-2 mb-3">
            <AlertCircle className="text-red-400" size={20} />
            <h3 className="text-lg font-semibold text-white">Clear all data?</h3>
          </div>
          <p className="text-sm text-slate-400 mb-2">
            This wipes ALL Production data for tenant
            <code className="px-1 py-0.5 mx-1 rounded bg-slate-800 text-slate-200 font-mono">{slug}</code>:
            items, recipes, products, and forecast.
          </p>
          <p className="text-sm text-amber-300 mb-6">
            Other tenants' data is unaffected. Cannot be undone.
          </p>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setConfirmClear(false)}
              className="px-4 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-white text-sm"
            >
              Cancel
            </button>
            <button
              onClick={handleClearConfirm}
              className="px-4 py-2 rounded-lg bg-red-500 hover:bg-red-400 text-white text-sm font-medium"
            >
              Clear &amp; reload
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function ActionTile({
  onClick, icon: Icon, title, description, accent,
}: {
  onClick: () => void;
  icon: React.ComponentType<{ size?: number }>;
  title: string;
  description: string;
  accent: 'emerald' | 'blue' | 'red' | 'orange';
}) {
  const accentClass = {
    emerald: 'text-emerald-300 hover:bg-emerald-500/10 hover:border-emerald-500/30',
    blue:    'text-blue-300 hover:bg-blue-500/10 hover:border-blue-500/30',
    red:     'text-red-300 hover:bg-red-500/10 hover:border-red-500/30',
    orange:  'text-orange-300 hover:bg-orange-500/10 hover:border-orange-500/30',
  }[accent];
  return (
    <button
      onClick={onClick}
      className={`p-4 rounded-2xl bg-slate-800/30 border border-slate-700/40 transition-all text-left ${accentClass}`}
    >
      <Icon size={18} />
      <div className="text-white font-medium mt-2">{title}</div>
      <div className="text-xs text-slate-400 mt-1 leading-snug">{description}</div>
    </button>
  );
}
